import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { getApiErrorMessage } from '@/lib/utils';
import { devicesApi, devicesQueryKeys } from '../api';
import type { DeviceFilters } from '../types';

// Hook for fetching devices list
export function useDevices(filters?: DeviceFilters) {
  return useQuery({
    queryKey: devicesQueryKeys.list(filters),
    queryFn: () => devicesApi.getAll(filters),
  });
}

// Hook for fetching single device
export function useDevice(id: number | string) {
  return useQuery({
    queryKey: devicesQueryKeys.detail(id),
    queryFn: () => devicesApi.getById(id),
    enabled: !!id,
  });
}

// Hook for deleting device
export function useDeleteDevice() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (id: number | string) => devicesApi.delete(id),
    onSuccess: () => {
      toast.success(t('deviceDeletedSuccess'));
      queryClient.invalidateQueries({ queryKey: devicesQueryKeys.lists() });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('errorDeletingDevice', { error: 'Unknown error' })));
    },
  });
}

// Hook for activating device
export function useActivateDevice() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (id: number | string) => devicesApi.activate(id),
    onSuccess: () => {
      toast.success(t('deviceActivatedSuccess'));
      queryClient.invalidateQueries({ queryKey: devicesQueryKeys.lists() });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('errorActivatingDevice', { error: 'Unknown error' })));
    },
  });
}

// Hook for blocking device
export function useBlockDevice() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (id: number | string) => devicesApi.block(id),
    onSuccess: () => {
      toast.success(t('deviceBlockedSuccess'));
      queryClient.invalidateQueries({ queryKey: devicesQueryKeys.lists() });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('errorBlockingDevice', { error: 'Unknown error' })));
    },
  });
}
